import { useState, useMemo, useCallback, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { type Reference } from '@apollo/client';
import {
  Alert,
  Box,
  Button,
  Skeleton,
  Stack,
  Typography,
  TextField,
  MenuItem,
} from '@mui/material';
import {
  useBoardQuery,
  useBoardUpdateMutation,
  useBoardDeleteMutation,
  useBoardColumnCreateMutation,
  useBoardColumnUpdateMutation,
  useBoardColumnDeleteMutation,
  useBoardColumnMoveMutation,
  useBoardCardCreateMutation,
  useBoardCardDeleteMutation,
  useBoardCardMoveMutation,
  useBoardMemberInviteMutation,
  BoardDocument,
  BoardsDocument,
  type BoardsQueryData,
} from '../generated/graphql';
import { useAuth } from '../common/auth/hooks/useAuth';
import { useNotification } from '../common/components/notification/NotificationContext';
import { BoardHeader } from '../common/components/boards/BoardHeader';
import FadeContent from '../common/components/reactBits/FadeContent';
import {
  BoardFilterBar,
  type BoardFilterState,
} from '../common/components/boards/BoardFilterBar';
import { BoardCanvas } from '../common/components/boards/BoardCanvas';
import { BoardCardDragPreview } from '../common/components/boards/BoardCardDragPreview';
import { BoardColumnDragPreview } from '../common/components/boards/BoardColumnDragPreview';
import { SortableBoardColumn } from '../common/components/boards/SortableBoardColumn';
import {
  useBoardDragHandlers,
  type CardMoveArgs,
  type ColumnMoveArgs,
} from '../common/components/boards/useBoardDragHandlers';
import {
  sortedByPosition,
  tailCardId,
} from '../common/components/boards/board-order';
import { dragTrace } from '../common/components/boards/drag-trace';
import { useBoardCardSlotting } from '../common/components/boards/useBoardCardSlotting';
import { useBoardColumnOrder } from '../common/components/boards/useBoardColumnOrder';
import {
  cardIdOf,
  columnIdOf,
  isCardDragId,
  isColumnDragId,
} from '../common/components/boards/drag-ids';
import {
  predictCardPosition,
  predictColumnPosition,
  rewireCardColumnsInCache,
  writeCardPositionsInCache,
  writeColumnPositionsInCache,
} from '../common/components/boards/board-cache';
import { AddProjectToBoardDialog } from '../common/components/boards/AddProjectToBoardDialog';
import { InviteBoardMemberDialog } from '../common/components/boards/InviteBoardMemberDialog';

interface BoardPageProps {
  currentUserId?: string;
}

export function BoardPage({ currentUserId }: BoardPageProps) {
  const navigate = useNavigate();
  const { userId } = useAuth();
  const resolvedCurrentUserId = currentUserId ?? userId ?? '';
  const { boardId } = useParams<{ boardId: string }>();
  useEffect(() => {
    // A board should not inherit a scrolled overview's position under the app bar.
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, [boardId]);
  const { data, loading, error, refetch } = useBoardQuery({
    variables: { id: boardId ?? '' },
    skip: !boardId,
    // Returning from a project page must refresh edits/deletions made there.
    fetchPolicy: 'cache-and-network',
  });

  const refetchOpts = useMemo(
    () => ({
      refetchQueries: [
        { query: BoardDocument, variables: { id: boardId ?? '' } },
      ],
    }),
    [boardId]
  );

  // showError needs to be in scope for the drag mutations' onError
  // handlers, so useNotification runs before the mutation hooks.
  const { showError } = useNotification();

  const [updateBoard] = useBoardUpdateMutation(refetchOpts);
  const [deleteBoard] = useBoardDeleteMutation();
  const [columnCreate] = useBoardColumnCreateMutation();
  const [columnUpdate] = useBoardColumnUpdateMutation();
  const [columnDelete] = useBoardColumnDeleteMutation();
  // Drag mutations: NO refetch — Apollo's optimisticResponse + update
  // callback update the cache synchronously and roll back on failure.
  // A refetch here would flash the whole board after every drag.
  const [columnMove] = useBoardColumnMoveMutation({
    onError: () =>
      showError(
        "Couldn't move the column. The original order has been restored."
      ),
  });
  const [cardMove] = useBoardCardMoveMutation({
    onError: () =>
      showError(
        "Couldn't move the card. The original position has been restored."
      ),
  });
  // Card creation also avoids a refetch: the update callback wires both
  // optimistic and server responses into the target column in place.
  const [cardCreate] = useBoardCardCreateMutation({
    onError: () =>
      showError("Couldn't add the project to the board. Please try again."),
    update(cache, { data: mutationData }, { variables }) {
      const createdCard = mutationData?.boardCardCreate;
      const columnId = variables?.input?.columnId;
      if (!createdCard || !columnId) return;
      const cardCacheId = cache.identify({
        __typename: 'BoardCard',
        _id: createdCard._id,
      });
      const columnCacheId = cache.identify({
        __typename: 'BoardColumn',
        _id: columnId,
      });
      if (!cardCacheId || !columnCacheId) return;
      cache.modify({
        id: columnCacheId,
        fields: {
          cards(existing, { readField, toReference }) {
            const refs = (
              Array.isArray(existing) ? existing : []
            ) as readonly Reference[];
            if (
              refs.some(
                (reference) => readField('_id', reference) === createdCard._id
              )
            ) {
              return refs;
            }
            const cardRef = toReference(cardCacheId);
            return cardRef ? [...refs, cardRef] : refs;
          },
        },
      });
    },
  });
  const [cardDelete] = useBoardCardDeleteMutation();
  const [memberInvite] = useBoardMemberInviteMutation(refetchOpts);

  const [inviteOpen, setInviteOpen] = useState(false);
  const [addProjectColumnId, setAddProjectColumnId] = useState<string | null>(
    null
  );
  const location = useLocation();
  const [editColumnsMode, setEditColumnsMode] = useState(false);
  const [mobileStageId, setMobileStageId] = useState<string | null>(null);
  const [filters, setFilters] = useState<BoardFilterState>({
    text: '',
    ownerId: null,
  });

  const ordered = useMemo(
    () =>
      (data?.board?.columns ?? [])
        .slice()
        .sort((a, b) => a.position - b.position),
    [data]
  );
  const columnsForMenu = useMemo(
    () => ordered.map((column) => ({ _id: column._id, name: column.name })),
    [ordered]
  );

  const filteredOrdered = useMemo(() => {
    const search = filters.text.trim().toLocaleLowerCase();

    return ordered.map((column) => ({
      ...column,
      cards: column.cards
        .filter((card) => {
          const matchesText =
            !search ||
            (card.project?.name ?? '').toLocaleLowerCase().includes(search);
          const matchesOwner =
            !filters.ownerId || card.addedBy === filters.ownerId;
          return matchesText && matchesOwner;
        })
        // Cards always render in manual order. The drag handlers and the
        // optimistic cache both compute `position`, so this is the single
        // ordering the whole board agrees on.
        .sort((a, b) => a.position - b.position),
    }));
  }, [filters, ordered]);

  // Holds the column order across a drop so dnd-kit does not animate the
  // columns back to their old slots while the Apollo write lands.
  const columnOrder = useBoardColumnOrder({ columns: filteredOrdered });

  const findCard = useCallback(
    (cardId: string) =>
      ordered.flatMap((col) => col.cards).find((k) => k._id === cardId),
    [ordered]
  );

  const renderDragOverlay = useCallback(
    (dragId: string) => {
      if (isCardDragId(dragId)) {
        const card = findCard(cardIdOf(dragId));
        if (!card) return null;
        return (
          <BoardCardDragPreview
            projectName={card.project?.name ?? 'Untitled card'}
          />
        );
      }
      if (isColumnDragId(dragId)) {
        const col = ordered.find((c) => c._id === columnIdOf(dragId));
        if (!col) return null;
        return (
          <BoardColumnDragPreview
            name={col.name}
            cardCount={col.cards.length}
          />
        );
      }
      return null;
    },
    [ordered, findCard]
  );

  const getItemLabel = useCallback(
    (dragId: string) => {
      if (isColumnDragId(dragId)) {
        return (
          ordered.find((c) => c._id === columnIdOf(dragId))?.name ?? 'column'
        );
      }
      if (isCardDragId(dragId)) {
        const card = findCard(cardIdOf(dragId));
        if (card) return `card "${card.project?.name ?? 'untitled'}"`;
        return 'card';
      }
      return dragId;
    },
    [ordered, findCard]
  );

  const excludeProjectIds = useMemo(
    () =>
      (data?.board?.columns ?? []).flatMap((c) =>
        c.cards.map((k) => k.projectId)
      ),
    [data?.board?.columns]
  );

  // Drag handlers use Apollo's optimisticResponse pattern. The predicted
  // position is written into the normalized cache immediately (so the
  // UI doesn't visually revert between drop and server response), and
  // Apollo auto-rolls back both the response and the update-callback
  // edits if the mutation rejects — `onError` on the mutation hook then
  // surfaces the failure toast. For cross-column card moves the
  // `update` callback rewires the source/target BoardColumn.cards lists
  // (Apollo won't do that from the returned BoardCard alone). Column
  // moves don't need list rewiring — `ordered` re-sorts by position.
  // Declared before early-returns so hook order stays unconditional.
  const onDragEndCardMove = useCallback(
    ({ cardId, toColumnId, afterCardId }: CardMoveArgs) => {
      const board = data?.board;
      if (!board) return;

      let currentCard:
        | (typeof board.columns)[number]['cards'][number]
        | undefined;
      for (const col of board.columns) {
        const found = col.cards.find((c) => c._id === cardId);
        if (found) {
          currentCard = found;
          break;
        }
      }
      if (!currentCard) return;

      const predicted = predictCardPosition(board, {
        cardId,
        toColumnId,
        afterCardId,
      });
      if (predicted === undefined) return;

      cardMove({
        variables: {
          input: { boardId: board._id, cardId, toColumnId, afterCardId },
        },
        optimisticResponse: {
          boardCardMove: { ...currentCard, position: predicted },
        },
        update(cache) {
          rewireCardColumnsInCache(cache, {
            cardId,
            toColumnId,
            columnIds: board.columns.map((column) => column._id),
          });
          // rewire only moves the card between columns. Positions still need
          // writing, and on a restripe that means every card in the column,
          // not just this one — otherwise the restripe never lands and the
          // column's positions stay clustered.
          writeCardPositionsInCache(cache, board, {
            cardId,
            toColumnId,
            afterCardId,
          });
        },
      });
    },
    [cardMove, data?.board]
  );

  const onMoveCardToColumn = useCallback(
    (cardId: string, toColumnId: string) => {
      const board = data?.board;
      if (!board) return;
      const target = board.columns.find((column) => column._id === toColumnId);
      onDragEndCardMove({
        cardId,
        toColumnId,
        afterCardId: tailCardId(target?.cards ?? []),
      });
    },
    [data?.board, onDragEndCardMove]
  );

  const onDragEndColumnMove = useCallback(
    ({ columnId, afterColumnId }: ColumnMoveArgs) => {
      const board = data?.board;
      if (!board) return;

      const currentColumn = board.columns.find((c) => c._id === columnId);
      if (!currentColumn) return;

      const predicted = predictColumnPosition(board, {
        columnId,
        afterColumnId,
      });
      dragTrace('colmov', {
        column: columnId,
        after: afterColumnId,
        wasPos: currentColumn.position,
        predicted,
        hasTypename: '__typename' in currentColumn,
        before: sortedByPosition(board.columns)
          .map((c) => c._id)
          .join('>'),
      });
      if (predicted === undefined) return;

      // Render the new order NOW. Without this the board keeps rendering the
      // old order until Apollo's write produces a frame, and dnd-kit animates
      // every shifted column back to where it started first.
      columnOrder.applyMove({ columnId, afterColumnId });

      columnMove({
        variables: {
          input: { boardId: board._id, columnId, afterColumnId },
        },
        optimisticResponse: {
          boardColumnMove: { ...currentColumn, position: predicted },
        },
        // optimisticResponse can only carry the moving column. On a restripe
        // every column's position changes, so write them all — otherwise the
        // row mixes restriped and stale coordinates.
        update(cache) {
          writeColumnPositionsInCache(cache, board, {
            columnId,
            afterColumnId,
          });
        },
      });
    },
    [columnMove, data?.board, columnOrder]
  );

  // Stable handlers passed down to SortableBoardColumn / SortableBoardCard
  // (both React.memo). Inline arrows in JSX would defeat memoization and
  // re-render the entire card grid on every parent render.
  const onAddCard = useCallback(
    (columnId: string) => setAddProjectColumnId(columnId),
    []
  );
  const onOpenProject = useCallback(
    (projectId: string) =>
      navigate(`/projects/${encodeURIComponent(projectId)}`, {
        state: { from: `${location.pathname}${location.search}` },
      }),
    [navigate, location.pathname, location.search]
  );
  const onRenameColumn = useCallback(
    (columnId: string, name: string) => {
      const board = data?.board;
      if (!board) return;
      const column = board.columns.find((item) => item._id === columnId);
      if (!column) return;
      columnUpdate({
        variables: { input: { boardId: board._id, columnId, name } },
        optimisticResponse: {
          boardColumnUpdate: { ...column, name },
        },
      });
    },
    [columnUpdate, data?.board]
  );
  const onDeleteColumn = useCallback(
    (columnId: string) => {
      const board = data?.board;
      if (!board || !board.columns.some((item) => item._id === columnId)) {
        return;
      }
      columnDelete({
        variables: { input: { boardId: board._id, columnId } },
        optimisticResponse: { boardColumnDelete: true },
        update(cache, { data: mutationData }) {
          if (!mutationData?.boardColumnDelete) return;
          const boardCacheId = cache.identify({
            __typename: 'Board',
            _id: board._id,
          });
          if (boardCacheId) {
            cache.modify({
              id: boardCacheId,
              fields: {
                columns(existing, { readField }) {
                  const refs = (
                    Array.isArray(existing) ? existing : []
                  ) as readonly Reference[];
                  return refs.filter(
                    (ref) => readField('_id', ref) !== columnId
                  );
                },
              },
            });
          }
          const columnCacheId = cache.identify({
            __typename: 'BoardColumn',
            _id: columnId,
          });
          if (columnCacheId) cache.evict({ id: columnCacheId });
        },
      });
    },
    [columnDelete, data?.board]
  );
  const onDeleteCard = useCallback(
    (cardId: string) => {
      const board = data?.board;
      if (!board) return;
      const column = board.columns.find((item) =>
        item.cards.some((card) => card._id === cardId)
      );
      if (!column) return;
      cardDelete({
        variables: { input: { boardId: board._id, cardId } },
        optimisticResponse: { boardCardDelete: true },
        update(cache, { data: mutationData }) {
          if (!mutationData?.boardCardDelete) return;
          const columnCacheId = cache.identify({
            __typename: 'BoardColumn',
            _id: column._id,
          });
          if (columnCacheId) {
            cache.modify({
              id: columnCacheId,
              fields: {
                cards(existing, { readField }) {
                  const refs = (
                    Array.isArray(existing) ? existing : []
                  ) as readonly Reference[];
                  return refs.filter((ref) => readField('_id', ref) !== cardId);
                },
              },
            });
          }
          const cardCacheId = cache.identify({
            __typename: 'BoardCard',
            _id: cardId,
          });
          if (cardCacheId) cache.evict({ id: cardCacheId });
        },
      });
    },
    [cardDelete, data?.board]
  );

  const { onDragEnd: fallbackDragEnd } = useBoardDragHandlers({
    board: data?.board ?? null,
    onCardMove: onDragEndCardMove,
    onColumnMove: onDragEndColumnMove,
  });
  const cardSlotting = useBoardCardSlotting({
    columns: columnOrder.displayColumns,
    onCardMove: onDragEndCardMove,
    fallbackDragEnd,
  });

  // ONE view drives both the SortableContext items and the rendered children.
  // Previously `columnIds` came from `ordered` while the children came from
  // the slotting view, so the two could describe different orders.
  const displayColumns = cardSlotting.displayColumns;
  const columnIds = useMemo(
    () => displayColumns.map((c) => c._id),
    [displayColumns]
  );

  // Traces WHEN the rendered order actually changes, so a drop and its
  // re-render can be compared by timestamp.
  useEffect(() => {
    dragTrace('order', { ids: columnIds.join('>') });
  }, [columnIds]);

  const onDragCancel = useCallback(() => {
    cardSlotting.onDragCancel();
    columnOrder.onDragCancel();
  }, [cardSlotting, columnOrder]);

  if (!boardId) {
    return (
      <Box p={3}>
        <Alert severity="error">Missing board id in URL.</Alert>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box data-testid="board-skeleton">
        <Box p={2} borderBottom={1} borderColor="divider">
          <Skeleton variant="text" width={240} height={36} />
          <Stack direction="row" spacing={1} mt={1}>
            <Skeleton variant="rounded" width={80} height={24} />
            <Skeleton variant="rounded" width={80} height={24} />
          </Stack>
        </Box>
        <Box sx={{ overflowX: 'auto', py: 2, px: 1 }}>
          <Stack direction="row" spacing={2}>
            {[0, 1, 2, 3].map((i) => (
              <Box key={i} sx={{ width: 280 }}>
                <Skeleton
                  variant="text"
                  width={120}
                  height={28}
                  sx={{ mb: 1 }}
                />
                <Skeleton variant="rounded" height={60} sx={{ mb: 1 }} />
                <Skeleton variant="rounded" height={60} sx={{ mb: 1 }} />
              </Box>
            ))}
          </Stack>
        </Box>
      </Box>
    );
  }

  if (error || !data?.board) {
    return (
      <Box p={3}>
        <Alert severity="error">We couldn't load this board.</Alert>
      </Box>
    );
  }

  const board = data.board;

  return (
    <Box
      key={board._id}
      sx={{
        width: '100%',
        mx: 'auto',
        boxSizing: 'border-box',
        py: { xs: 1.5, sm: 1 },
        px: { xs: 0, sm: 2 },
        pr: { xs: 3.5, sm: 2 },
        minWidth: 0,
      }}
    >
      <FadeContent playOnMount duration={420} data-board-entrance="header">
        <BoardHeader
          board={board}
          currentUserId={resolvedCurrentUserId}
          onRename={(name) =>
            updateBoard({ variables: { input: { id: board._id, name } } })
          }
          onInvite={() => setInviteOpen(true)}
          onReorganizeColumns={() => setEditColumnsMode(true)}
          onDelete={async () => {
            if (
              !window.confirm(
                "Delete this board permanently? This can't be undone."
              )
            ) {
              return;
            }
            await deleteBoard({
              variables: { input: { id: board._id } },
              update(cache) {
                const prev = cache.readQuery<BoardsQueryData>({
                  query: BoardsDocument,
                });
                if (prev) {
                  cache.writeQuery<BoardsQueryData>({
                    query: BoardsDocument,
                    data: {
                      boards: prev.boards.filter((b) => b._id !== board._id),
                    },
                  });
                }
                cache.evict({
                  id: cache.identify({ __typename: 'Board', _id: board._id }),
                });
                cache.gc();
              },
            });
            navigate('/boards');
          }}
        />
      </FadeContent>

      <FadeContent
        playOnMount
        duration={400}
        delay={70}
        data-board-entrance="toolbar"
      >
        <BoardFilterBar board={board} value={filters} onChange={setFilters} />
      </FadeContent>

      {editColumnsMode && (
        <Stack
          direction="row"
          alignItems="center"
          spacing={2}
          sx={{
            px: 2,
            py: 1,
            backgroundColor: 'action.selected',
            borderBottom: 1,
            borderColor: 'divider',
          }}
        >
          <Typography variant="body2" sx={{ flex: 1 }}>
            Editing columns — rename, add, delete, or drag to reorder.
          </Typography>
          <Button
            size="small"
            variant="contained"
            onClick={() => setEditColumnsMode(false)}
          >
            Done
          </Button>
        </Stack>
      )}

      {!editColumnsMode && displayColumns.length > 0 && (
        <Box sx={{ display: { xs: 'block', md: 'none' }, mt: 1 }}>
          <TextField
            select
            fullWidth
            size="small"
            label="Stage"
            id="board-stage-select"
            value={
              displayColumns.some((column) => column._id === mobileStageId)
                ? mobileStageId
                : displayColumns[0]._id
            }
            onChange={(event) => setMobileStageId(event.target.value)}
          >
            {displayColumns.map((column) => (
              <MenuItem key={column._id} value={column._id}>
                {column.name} ({column.cards.length})
              </MenuItem>
            ))}
          </TextField>
          <Typography variant="caption" color="text.secondary">
            Switch stages here. Use a card’s menu to move it.
          </Typography>
        </Box>
      )}
      <BoardCanvas
        columnIds={columnIds}
        onDragStart={cardSlotting.onDragStart}
        onDragOver={cardSlotting.onDragOver}
        onDragEnd={cardSlotting.onDragEnd}
        onDragCancel={onDragCancel}
        getItemLabel={getItemLabel}
        renderDragOverlay={renderDragOverlay}
        onAddColumn={
          editColumnsMode
            ? () => {
                const tempColumnId = `temp-${crypto.randomUUID()}`;
                const position =
                  (ordered[ordered.length - 1]?.position ?? 0) + 1024;
                columnCreate({
                  variables: {
                    input: { boardId: board._id, name: 'New column' },
                  },
                  optimisticResponse: {
                    boardColumnCreate: {
                      __typename: 'BoardColumn',
                      _id: tempColumnId,
                      name: 'New column',
                      position,
                      cards: [],
                    },
                  },
                  update(cache, { data: mutationData }) {
                    const createdColumn = mutationData?.boardColumnCreate;
                    if (!createdColumn) return;
                    const columnCacheId = cache.identify(createdColumn);
                    const boardCacheId = cache.identify({
                      __typename: 'Board',
                      _id: board._id,
                    });
                    if (!columnCacheId || !boardCacheId) return;
                    cache.modify({
                      id: boardCacheId,
                      fields: {
                        columns(existing, { readField, toReference }) {
                          const refs = (
                            Array.isArray(existing) ? existing : []
                          ) as readonly Reference[];
                          const columnRef = toReference(columnCacheId);
                          if (!columnRef) return refs;
                          if (
                            refs.some(
                              (ref) =>
                                readField('_id', ref) === createdColumn._id
                            )
                          ) {
                            return refs;
                          }
                          return [...refs, columnRef];
                        },
                      },
                    });
                  },
                });
              }
            : undefined
        }
      >
        {displayColumns.map((col, index) => (
          <SortableBoardColumn
            key={col._id}
            mobileActive={
              editColumnsMode ||
              col._id ===
                (displayColumns.some((column) => column._id === mobileStageId)
                  ? mobileStageId
                  : displayColumns[0]?._id)
            }
            entranceDelay={140 + Math.min(index, 5) * 65}
            column={col}
            columns={columnsForMenu}
            dragEnabled={editColumnsMode}
            onRenameColumn={onRenameColumn}
            onDeleteColumn={editColumnsMode ? onDeleteColumn : undefined}
            onAddCard={onAddCard}
            onDeleteCard={onDeleteCard}
            onMoveCard={onMoveCardToColumn}
            onOpenProject={onOpenProject}
          />
        ))}
      </BoardCanvas>

      <InviteBoardMemberDialog
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
        onSubmit={(userIdOrEmail) => {
          memberInvite({
            variables: { input: { boardId: board._id, userIdOrEmail } },
          });
          setInviteOpen(false);
        }}
      />
      {addProjectColumnId && (
        <AddProjectToBoardDialog
          open
          onClose={() => setAddProjectColumnId(null)}
          excludeProjectIds={excludeProjectIds}
          onSubmit={(project) => {
            const variables = {
              input: {
                boardId: board._id,
                columnId: addProjectColumnId,
                projectId: project._id,
              },
            };
            const targetColumn = board.columns.find(
              (column) => column._id === addProjectColumnId
            );

            if (!targetColumn) {
              cardCreate({ variables });
              setAddProjectColumnId(null);
              return;
            }

            // Append after the current tail so the optimistic card slots in at
            // the bottom of the column, matching the server's default ordering.
            const cardsByPosition = targetColumn.cards
              .slice()
              .sort((a, b) => a.position - b.position);
            const position =
              (cardsByPosition[cardsByPosition.length - 1]?.position ?? 0) +
              1024;
            const tempCardId = `temp-${crypto.randomUUID()}`;

            cardCreate({
              variables,
              optimisticResponse: {
                boardCardCreate: {
                  __typename: 'BoardCard',
                  _id: tempCardId,
                  projectId: project._id,
                  position,
                  addedAt: new Date().toISOString(),
                  addedBy: resolvedCurrentUserId,
                  project: {
                    __typename: 'ProjectType',
                    _id: project._id,
                    name: project.name,
                    versionCount: project.versionCount,
                    ownerUser: {
                      __typename: 'UserType',
                      _id: project.ownerUser._id,
                      firstName: project.ownerUser.firstName,
                      lastName: project.ownerUser.lastName,
                    },
                  },
                },
              },
            });
            setAddProjectColumnId(null);
          }}
        />
      )}
    </Box>
  );
}
